/**
 * Path Reinforcement Tool - BIM 2025 Grade
 * 
 * Creates linear rebar distributions along a path (e.g., Wall/Floor edges).
 * Features:
 * - Main Bars + Alternating Bars support
 * - Hook settings per bar type
 * - Spacing rules
 * - Face options (Top/Bottom/Both)
 */

import { v4 as uuidv4 } from 'uuid';
import { Element } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

export interface PathBarDefinition {
    barType: string; // e.g. #5 or Ø16
    spacing: number;
    length?: number; // Automatic or fixed
    hookStart: string;
    hookEnd: string;
}

export interface PathReinforcementGeometryData {
    hostId: string;
    path: THREE.Vector3[]; // Polyline path
    primaryBars: PathBarDefinition;
    alternatingBars?: PathBarDefinition;
    faces: 'Top' | 'Bottom' | 'Both';
}

export interface PathReinforcementElement extends Element {
    category: 'Structural Path Reinforcement';
    geometryData: PathReinforcementGeometryData;
}

export class PathReinforcementTool extends BaseTool {
    id = 'path_reinforcement';
    name = 'Path Reinforcement';
    cursor = 'crosshair';

    private hostId: string | null = null;
    private pathPoints: THREE.Vector3[] = [];

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        // Auto-detect host (manual scene raycast — element meshes carry no
        // R3F handlers, so event.intersections never resolves BIM elements)
        if (!this.hostId) {
            const hit = this.raycastSceneAtEvent(event).find(i => {
                const id = this.findId(i.object);
                if (!id) return false;
                const el = this.context!.bimStore.project.elements.get(id);
                return el && (el.category === 'Floors' || el.category === 'Walls' || el.category === 'Structural Foundations');
            });

            if (hit) {
                this.hostId = this.findId(hit.object)!;
                console.log(`Host selected: ${this.hostId}`);
            }
            return;
        }

        // Add points to path
        const pt = this.getIntersection(event);
        if (pt) {
            this.pathPoints.push(this.snapToGrid(pt));
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const pt = this.getIntersection(event);
        if (pt && this.pathPoints.length > 0) {
            this.context.appStore.setToolPreview({
                type: 'polyline',
                points: [...this.pathPoints, pt],
                color: '#00FFFF' // Cyan for path
            });
        }
    }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter' && this.pathPoints.length >= 2) {
            this.createPathReinforcement();
        } else if (event.key === 'Escape') {
            this.reset();
        }
    }

    private reset() {
        this.hostId = null;
        this.pathPoints = [];
        this.context?.appStore.setToolPreview(null);
    }

    private createPathReinforcement() {
        if (!this.context || !this.hostId) return;

        const geometryData: PathReinforcementGeometryData = {
            hostId: this.hostId,
            path: this.pathPoints,
            primaryBars: {
                barType: '#5',
                spacing: 200,
                hookStart: 'None',
                hookEnd: 'None'
            },
            faces: 'Top'
        };

        const element: PathReinforcementElement = {
            id: uuidv4(),
            category: 'Structural Path Reinforcement',
            type: 'Path Reinforcement',
            parameters: {
                'Primary Spacing': 200,
                'Primary Type': '#5',
                'Include Alternating': 'No'
            },
            constraints: [],
            geometryData
        };

        this.context.bimStore.addElement(element);
        this.reset();
    }

    onPointerUp() { }
}
